import { useEffect, useState } from "react";

export type LandingMode = "contract" | "ipc" | "payments" | "review" | "scan";

const CARDS: { mode: LandingMode; no: string; title: string; desc: string }[] = [
  { mode: "contract", no: "01", title: "Contract Studio", desc: "Draft & assemble FIDIC Red Book and AIA construction contracts, particular conditions and appendices." },
  { mode: "ipc", no: "02", title: "Payment Certificates", desc: "Certify interim, advance and final payments — scan an invoice and the certificate prepares itself." },
  { mode: "payments", no: "03", title: "Payments Ledger", desc: "Track payments, advances and deposits by contract, with live currency and budget burn-down." },
  { mode: "scan", no: "04", title: "Conflict Scanner", desc: "Upload a contract and the AI flags internal conflicts, ambiguities, risky terms and missing protections." },
  { mode: "review", no: "05", title: "Review & Sign-off", desc: "Circulate a document to the parties for comment, agreement and execution." },
];

export function Landing({ onEnter }: { onEnter: (mode: LandingMode) => void }) {
  const [phase, setPhase] = useState<"load" | "ready">("load");
  const [portal, setPortal] = useState(false);
  const [link, setLink] = useState("");
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setPhase("ready"), 1750);
    return () => clearTimeout(t);
  }, []);

  function openPortal() {
    const v = link.trim();
    if (!v) { setErr("Paste the link (or code) you were sent."); return; }
    // A full URL → go straight there.
    try {
      const u = new URL(v);
      if (u.searchParams.get("review") || u.searchParams.get("ledger")) { window.location.href = u.toString(); return; }
    } catch { /* not a URL */ }
    // Bare code → assume a review portal link.
    if (/^[a-z0-9]{6,}$/i.test(v)) { window.location.search = `?review=${encodeURIComponent(v)}`; return; }
    setErr("That doesn’t look like a portal link or code.");
  }

  return (
    <div className={`landing landing--${phase}`}>
      {/* Preload */}
      <div className="ld-preload" aria-hidden={phase === "ready"}>
        <div className="ld-pre-mark">
          <span>Studio</span>
          <i className="ld-dot" />
          <span>Contract</span>
        </div>
        <div className="ld-pre-bar"><i /></div>
      </div>

      {/* Main */}
      <div className="ld-main">
        <header className="ld-head">
          <div className="ld-brand">Studio<span className="ld-dot2"> · </span>Contract</div>
          <button className="ld-signin" onClick={() => setPortal(true)}>Client portal →</button>
        </header>

        <div className="ld-hero">
          <h1 className="ld-title">
            <span className="ld-line"><span>Construction contracts,</span></span>
            <span className="ld-line"><span>certified & tracked.</span></span>
          </h1>
          <p className="ld-lede">Author FIDIC &amp; AIA contracts, certify every payment, and give your clients a window into their project — in one studio.</p>
          <div className="ld-choose">Choose where to go</div>
        </div>

        <div className="ld-cards">
          {CARDS.map((c, i) => (
            <button key={c.mode} className="ld-card" style={{ ["--i" as any]: i }} onClick={() => onEnter(c.mode)}>
              <span className="ld-card-no">{c.no}</span>
              <span className="ld-card-body">
                <span className="ld-card-title">{c.title}</span>
                <span className="ld-card-desc">{c.desc}</span>
              </span>
              <span className="ld-card-arrow">→</span>
            </button>
          ))}
          <button className="ld-card ld-card--portal" style={{ ["--i" as any]: CARDS.length }} onClick={() => setPortal(true)}>
            <span className="ld-card-no">◍</span>
            <span className="ld-card-body">
              <span className="ld-card-title">Client Portal</span>
              <span className="ld-card-desc">Clients: open your project — review documents, comment and follow certified payments with the link you were sent.</span>
            </span>
            <span className="ld-card-arrow">→</span>
          </button>
        </div>
      </div>

      {portal && (
        <div className="ld-modal-back" onClick={() => setPortal(false)}>
          <div className="ld-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ld-modal-kicker">Client Portal</div>
            <h2 className="ld-modal-title">Open your project</h2>
            <p className="ld-modal-p">Paste the portal link (or code) your project team sent you to view documents, add comments, and follow certified payments.</p>
            <input className="ld-modal-input" value={link} autoFocus placeholder="https://…?review=… or paste your code"
              onChange={(e) => { setLink(e.target.value); setErr(null); }} onKeyDown={(e) => { if (e.key === "Enter") openPortal(); }} />
            {err && <div className="ld-modal-err">{err}</div>}
            <div className="ld-modal-actions">
              <button className="ld-btn-dark" onClick={openPortal}>Enter portal →</button>
              <button className="ld-btn-ghost" onClick={() => setPortal(false)}>Cancel</button>
            </div>
            <div className="ld-modal-note">No link yet? Ask your project team to share your portal from the Review or Payments screen.</div>
          </div>
        </div>
      )}
    </div>
  );
}
